/**
 * useAutoTranslate — translate arbitrary content text (place descriptions,
 * overview/details bodies, etc.) on the fly using the runtime translator.
 *
 * - Returns the English source instantly on SSR / first render.
 * - When the translation arrives, the provider re-renders all consumers via
 *   `useSyncExternalStore` wired in `LanguageContext`.
 * - Caches in DB (server) + localStorage (client). Two devices viewing the
 *   same English text get the same translation with a single AI call.
 * - For long bodies it splits on blank-line paragraphs so each chunk fits the
 *   4000-char server limit.
 */

import { useSyncExternalStore } from "react";
import { useLanguage } from "./LanguageContext";
import { getRuntimeTranslation, getTranslationsVersion, subscribeToTranslations } from "./runtime-translate";

// Shared hydration flag (see useAutoTranslate below).
let hydratedFlag = false;
const hydratedListeners = new Set<() => void>();
function subscribeHydrated(cb: () => void) {
  hydratedListeners.add(cb);
  if (!hydratedFlag && typeof window !== "undefined") {
    hydratedFlag = true;
    // Notify on the next microtask so we never set state during render.
    queueMicrotask(() => hydratedListeners.forEach((l) => l()));
  }
  return () => hydratedListeners.delete(cb);
}



const MAX_CHUNK = 3500;

function hashKey(s: string): string {
  // tiny djb2 — only needs to be stable per browser session for queue dedupe;
  // the server cache is keyed by sha256 of the source text.
  let h = 5381;
  for (let i = 0; i < s.length; i++) h = ((h << 5) + h + s.charCodeAt(i)) | 0;
  return (h >>> 0).toString(36);
}

function chunk(text: string): string[] {
  if (text.length <= MAX_CHUNK) return [text];
  const paras = text.split(/\n\n+/);
  const out: string[] = [];
  let buf = "";
  for (const p of paras) {
    if ((buf + "\n\n" + p).length > MAX_CHUNK) {
      if (buf) out.push(buf);
      if (p.length > MAX_CHUNK) {
        // hard split very long single paragraph
        for (let i = 0; i < p.length; i += MAX_CHUNK) out.push(p.slice(i, i + MAX_CHUNK));
        buf = "";
      } else {
        buf = p;
      }
    } else {
      buf = buf ? buf + "\n\n" + p : p;
    }
  }
  if (buf) out.push(buf);
  return out;
}

export function useAutoTranslate(text: string | null | undefined): string {
  const { lang } = useLanguage();
  // Subscribe to runtime translation updates so this component re-renders
  // when a fresh translation arrives from the server.
  useSyncExternalStore(subscribeToTranslations, getTranslationsVersion, () => 0);

  // Avoid hydration mismatch: SSR has no localStorage, so it always renders
  // the English source. The client must do the same on first render and only
  // swap in cached/loaded translations after hydration.
  // One shared hydration flag for the whole app instead of per-instance
  // state — hundreds of <AutoT> nodes each scheduling their own setState on
  // mount caused a visible post-hydration flicker.
  const hydrated = useSyncExternalStore(subscribeHydrated, () => hydratedFlag, () => false);


  const source = (text ?? "").toString();
  if (!source.trim()) return "";
  if (lang === "en" || !hydrated) return source;

  const parts = chunk(source);
  const translated = parts.map((p) => {
    const key = `auto:${hashKey(p)}`;
    const t = getRuntimeTranslation(lang, key, p);
    return t ?? p;
  });
  return translated.join("\n\n");
}


/**
 * useAutoTranslator — like `useAutoTranslate`, but returns a translate
 * function so a component can localize many strings (labels, placeholders,
 * button captions) with a single hook call.
 */
export function useAutoTranslator(): (text: string | null | undefined) => string {
  const { lang } = useLanguage();
  useSyncExternalStore(subscribeToTranslations, getTranslationsVersion, () => 0);
  const hydrated = useSyncExternalStore(subscribeHydrated, () => hydratedFlag, () => false);

  return (text) => {
    const source = (text ?? "").toString();
    if (!source.trim()) return "";
    if (lang === "en" || !hydrated) return source;
    return chunk(source)
      .map((p) => getRuntimeTranslation(lang, `auto:${hashKey(p)}`, p) ?? p)
      .join("\n\n");
  };
}

/**
 * Proper nouns (venue, restaurant and business names) must never be machine
 * translated — "Robusto" was coming out as "Voimakas" in Finnish. Names are
 * rendered verbatim; a human-entered translation from `content_translations`
 * still wins where the caller supplies one.
 */
export function useProperName(text: string | null | undefined): string {
  return (text ?? "").toString();
}
